import {HttpClient} from "@angular/common/http";
import {TranslateLoader} from "@ngx-translate/core";
import {Observable, Subscription, interval, of, from} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {CacheService} from "../modules/shared/services/cache.service";
import {Translation} from "../interfaces/translation.interface";
import {AppService} from "./app.service";
import {environment} from "src/environments/environment";

export class TranslateCacheLoader implements TranslateLoader {
  private version = "1.0.0";
  private readonly subscriptions: Subscription[] = [];
  private readonly translationsVersion: string =
    environment.translationsVersion ?? "0";
  private readonly translationLanguages = new Set<string>();

  constructor(
    private readonly http: HttpClient,
    private readonly cacheService: CacheService,
    private readonly appService: AppService
  ) {
    this.initializeVersion();
  }

  private initializeVersion(): void {
    const getVersionSubscription = this.appService
      .getAppVersion()
      .pipe(
        tap((response) => {
          this.updateCache(response.version);
        }),
        catchError((error) => {
          console.error("Error retrieving app version, using cache:", error);

          this.cacheService
            .getFromCache("app_version")
            .then((cachedAppVersion) => {
              this.version = (cachedAppVersion as string) ?? "0";
            });
          return of(null);
        })
      )
      .subscribe();

    const getIntervalVersionSubscription = interval(60 * 60 * 1000)
      .pipe(switchMap(() => this.appService.getAppVersion()))
      .subscribe((response) => {
        this.updateCache(response.version);
      });

    this.subscriptions.push(
      getVersionSubscription,
      getIntervalVersionSubscription
    );
  }

  updateCache(version: string) {
    if (version && this.version !== version) {
      this.version = version;
      this.cacheService
        .addToCache(`app_version`, this.version, 43200)
        .catch((err) => console.error("Cache write failed:", err));
      this.translationLanguages.forEach((lang) => {
        const cacheKey = this.buildCacheKey(lang);
        this.updateTranslationsInBackground(lang, cacheKey);
      });
    }
  }

  getTranslation(lang: string): Observable<Translation> {
    const cacheKey = this.buildCacheKey(lang);
    this.translationLanguages.add(lang);

    return from(this.cacheService.getFromCache(cacheKey)).pipe(
      switchMap((cachedTranslations) => {
        if (cachedTranslations) {
          this.updateTranslationsInBackground(lang, cacheKey);
          return of(cachedTranslations as Translation);
        }
        return this.fetchTranslations(lang, cacheKey);
      })
    );
  }

  private fetchTranslations(
    lang: string,
    cacheKey: string
  ): Observable<Translation> {
    const url = this.buildTranslationUrl(lang);
    return this.http.get<Translation>(url).pipe(
      tap((translations) => {
        this.cacheService
          .clearCacheByPrefix(["translations_"])
          .catch((err) => console.error("Cache clear failed:", err));
        this.cacheService
          .addToCache(cacheKey, translations, 40320)
          .catch((err) => console.error("Cache write failed:", err));
      }),
      catchError((error) => {
        console.error(`Error during ${lang} translations loading:`, error);
        return from(this.cacheService.getFromCache(cacheKey)).pipe(
          map(
            (cachedTranslations) =>
              (cachedTranslations as Translation) ?? ({} as Translation)
          )
        );
      })
    );
  }

  private updateTranslationsInBackground(lang: string, cacheKey: string): void {
    const url = this.buildTranslationUrl(lang);
    this.http
      .get<Translation>(url)
      .pipe(
        tap((translations) => {
          this.cacheService
            .clearCacheByPrefix(["translations_"])
            .catch((err) => console.error("Cache clear failed:", err));
          this.cacheService
            .addToCache(cacheKey, translations, 40320)
            .catch((err) => console.error("Cache write failed:", err));
          this.appService.refreshTranslations(lang, translations);
        }),
        catchError((error) => {
          console.error(`Error during ${lang} translations update:`, error);
          return of(null);
        })
      )
      .subscribe();
  }

  private buildCacheKey(lang: string): string {
    return `translations_${lang}?v=${this.version}&t=${this.translationsVersion}`;
  }

  private buildTranslationUrl(lang: string): string {
    return `/assets/i18n/${lang}.json?v=${this.version}&t=${this.translationsVersion}`;
  }
}
